import { useState } from "react";
import { message } from "antd";
import { supabase } from "../lib/supabase";
import { BookingInfo } from "../types/container-unloading";
import type { ContainerUnloadingBooking } from "./useContainerUnloading";
import { hapticFeedback } from "../utils/containerUnloadingHelpers";

export const useProductUpdates = (
  bookings: ContainerUnloadingBooking[],
  setBookings: React.Dispatch<React.SetStateAction<ContainerUnloadingBooking[]>>,
  fetchContainerData: () => Promise<void>
) => {
  // LOCAL STATE FOR PENDING CHANGES (not saved to database until "Finish")
  const [pendingCartonUpdates, setPendingCartonUpdates] = useState<
    Record<number, number>
  >({});
  const [pendingPalletUpdates, setPendingPalletUpdates] = useState<
    Record<number, number>
  >({});
  const [pendingBookingPallets, setPendingBookingPallets] = useState<
    Record<number, number>
  >({});
  const [finishingBookings, setFinishingBookings] = useState<Set<number>>(
    new Set()
  );

  // Update product cartons - INSTANT database save
  const updateProductCartons = async (productId: number, cartons: number) => {
    // Save to database immediately
    const { error } = await supabase
      .from("booking_product")
      .update({ actual_cartons: cartons })
      .eq("id", productId);

    if (error) {
      message.error("Failed to update carton count");
      console.error("Error updating cartons:", error);
      return;
    }

    // Update local state for immediate UI feedback
    setBookings((prevBookings) =>
      prevBookings.map((booking) => ({
        ...booking,
        products: booking.products.map((product) =>
          product.id === productId
            ? {
                ...product,
                actual_cartons: cartons,
                completed:
                  cartons !== null && cartons === product.estimated_cartons,
              }
            : product
        ),
        unloading_status: (() => {
          const updatedProducts = booking.products.map((product) =>
            product.id === productId
              ? {
                  ...product,
                  actual_cartons: cartons,
                  completed:
                    cartons !== null && cartons === product.estimated_cartons,
                }
              : product
          );

          if (booking.unloading_status === "completed") return "completed";
          if (updatedProducts.some((p) => p.completed)) return "counting";
          return "pending";
        })(),
      }))
    );

    hapticFeedback();
  };

  const quickAddCartons = async (productId: number, amount: number) => {
    const product = bookings
      .flatMap((b) => b.products)
      .find((p) => p.id === productId);

    if (!product) return;

    const currentCartons = product.actual_cartons || 0;
    const newCartons = Math.max(0, currentCartons + amount);
    await updateProductCartons(productId, newCartons);
  };

  const setAllBoxesCounted = (productId: number) => {
    const product = bookings
      .flatMap((b) => b.products)
      .find((p) => p.id === productId);

    if (!product) return;

    updateProductCartons(productId, product.estimated_cartons);
  };

  const resetProductCount = (productId: number) => {
    updateProductCartons(productId, 0);
  };

  // Update product pallets in LOCAL STATE only (not saved to database until "Finish")
  const updateProductPallets = (productId: number, pallets: number) => {
    // Store the pending update
    setPendingPalletUpdates((prev) => ({
      ...prev,
      [productId]: pallets,
    }));

    // Update local state for immediate UI feedback
    setBookings((prevBookings) =>
      prevBookings.map((booking) => ({
        ...booking,
        products: booking.products.map((product) =>
          product.id === productId ? { ...product, pallets: pallets } : product
        ),
      }))
    );

    hapticFeedback();
  };

  // Quick add pallets for products
  const quickAddProductPallets = (productId: number, amount: number) => {
    const product = bookings
      .flatMap((b) => b.products)
      .find((p) => p.id === productId);
    if (product) {
      const currentPallets = product.pallets || 0;
      const newPallets = Math.max(0, currentPallets + amount);
      updateProductPallets(productId, newPallets);
    }
  };

  // Update booking-level pallets - INSTANT database save
  const updateBookingPallets = async (bookingId: number, pallets: number) => {
    // Save to database immediately
    const { error } = await supabase
      .from("bookings")
      .update({ total_actual_pallets: pallets })
      .eq("id", bookingId);

    if (error) {
      message.error("Failed to update pallet count");
      console.error("Error updating pallets:", error);
      return;
    }

    // Update local state for immediate UI feedback
    setBookings((prevBookings) =>
      prevBookings.map((booking) => {
        if (booking.id !== bookingId) return booking;
        return {
          ...booking,
          total_actual_pallets: pallets,
        };
      })
    );

    hapticFeedback();
  };

  // Quick add booking pallets
  const quickAddBookingPallets = async (bookingId: number, amount: number) => {
    const booking = bookings.find((b) => b.id === bookingId);
    if (booking) {
      const currentPallets = booking.total_actual_pallets || 0;
      const newPallets = Math.max(0, currentPallets + amount);
      await updateBookingPallets(bookingId, newPallets);
    }
  };

  // Check if booking is ready to finish (all cartons counted)
  const isBookingReadyToFinish = (booking: BookingInfo): boolean => {
    return booking.products.every((p) => p.completed);
  };

  // Check if booking has pending changes (now always false since we save instantly)
  const hasBookingPendingChanges = (booking: BookingInfo): boolean => {
    return false; // No pending changes anymore - everything saves instantly
  };

  // Auto-create delivery info when booking becomes delivery_ready
  const autoCreateDeliveryInfo = async (bookingId: number) => {
    try {
      const booking = bookings.find((b) => b.id === bookingId);
      if (!booking || !booking.has_delivery_address) return;

      // Check if delivery info already exists
      if (booking.delivery_info?.id) {
        // Update existing delivery info with actual counts
        const { error } = await supabase
          .from("booking_delivery")
          .update({
            delivery_cartons: booking.total_actual_cartons,
            delivery_pallets: booking.total_actual_pallets,
            shipment_status: "planned",
          })
          .eq("id", booking.delivery_info.id);

        if (error) throw error;
        console.log("Updated existing delivery info with actual counts");
      } else {
        console.log(
          "Delivery address exists but no delivery info record found - this shouldn't happen"
        );
      }
    } catch (error) {
      console.error("Error auto-creating delivery info:", error);
    }
  };

  // Finish booking - calculate totals and update status (data already saved instantly)
  const finishBooking = async (booking: BookingInfo) => {
    if (!isBookingReadyToFinish(booking)) {
      message.error("Not all products have been counted yet");
      return;
    }

    try {
      setFinishingBookings((prev) => new Set([...prev, booking.id]));

      // Calculate total actual cartons from current product counts
      const totalActualCartons = booking.products.reduce((sum, product) => {
        return sum + (product.actual_cartons ?? 0);
      }, 0);

      // Get booking-level pallets
      const bookingPallets = booking.total_actual_pallets || 0;

      // Determine final status
      // Both delivery and storage bookings go to "delivery_ready" after counting
      // Storage bookings only become "completed" after purchase order is created
      const finalStatus = "delivery_ready";

      // Update booking with totals and status
      const { error: bookingError } = await supabase
        .from("bookings")
        .update({
          total_actual_cartons: totalActualCartons,
          total_actual_pallets: bookingPallets,
          unloading_status: finalStatus,
        })
        .eq("id", booking.id);

      if (bookingError) throw bookingError;

      // Auto-create delivery info for delivery bookings
      if (booking.has_delivery_address) {
        await autoCreateDeliveryInfo(booking.id);
      }

      // Refresh data to get latest state
      await fetchContainerData();

      message.success(`Booking ${booking.booking_reference} completed!`);
    } catch (error) {
      console.error("Error finishing booking:", error);
      message.error("Failed to complete booking");
    } finally {
      setFinishingBookings((prev) => {
        const newSet = new Set(prev);
        newSet.delete(booking.id);
        return newSet;
      });
    }
  };

  // Reset booking to a specific step
  const resetBookingToStep = async (
    bookingId: number,
    step: "counting" | "delivery_ready"
  ) => {
    try {
      const updates: any = {
        unloading_status: step === "counting" ? null : step,
      };

      // Reset data based on step
      if (step === "counting") {
        // Reset all product counts for this booking
        const booking = bookings.find((b) => b.id === bookingId);
        if (booking) {
          for (const product of booking.products) {
            await supabase
              .from("booking_product")
              .update({ actual_cartons: null })
              .eq("id", product.id);
          }
        }
        updates.total_actual_cartons = null;
        updates.total_actual_pallets = null;
        updates.unloading_notes = null;
      }

      // Update booking
      const { error } = await supabase
        .from("bookings")
        .update(updates)
        .eq("id", bookingId);

      if (error) throw error;

      // Refresh data
      await fetchContainerData();

      message.success("Booking reset successfully");
    } catch (error) {
      console.error("Error resetting booking:", error);
      message.error("Failed to reset booking");
    }
  };

  // Mark booking as completed
  const markBookingAsCompleted = async (bookingId: number) => {
    try {
      const { error } = await supabase
        .from("bookings")
        .update({
          unloading_status: "completed",
          unloaded_at: new Date().toISOString(),
        })
        .eq("id", bookingId);

      if (error) throw error;

      // Update local state
      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking.id === bookingId
            ? { ...booking, unloading_status: "completed" as const }
            : booking
        )
      );

      message.success(
        "Booking marked as completed! Shipment created successfully. You can now download shipping labels."
      );

      // Refresh data to get latest state
      await fetchContainerData();
    } catch (error) {
      console.error("Error marking booking as completed:", error);
      message.error("Failed to mark booking as completed");
    }
  };

  return {
    // State
    pendingCartonUpdates,
    pendingPalletUpdates,
    pendingBookingPallets,
    finishingBookings,

    // Product carton operations
    updateProductCartons,
    quickAddCartons,
    setAllBoxesCounted,
    resetProductCount,

    // Product pallet operations
    updateProductPallets,
    quickAddProductPallets,

    // Booking pallet operations
    updateBookingPallets,
    quickAddBookingPallets,

    // Booking operations
    isBookingReadyToFinish,
    hasBookingPendingChanges,
    finishBooking,
    resetBookingToStep,
    markBookingAsCompleted,
  };
};
